import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  GiftCard,
  PatchAdminV1StoresStoreIdGiftCardsIdBody,
  PostAdminV1StoresStoreIdGiftCardsBody,
  PostAdminV1StoresStoreIdGiftCardsIdAdjustBody,
  PostAdminV1StoresStoreIdGiftCardsIdSendBody,
} from '@nucleus-commerce/api-client/admin';

import { giftCardsApi } from '../api/gift-cards-api';

import { giftCardKeys } from './gift-card-keys';

/**
 * The response carries the code once. It is handed to the dialog and deliberately not put in
 * the query cache, so it cannot resurface on another screen. The dialog mints one idempotency
 * key per opening, so a retry replays the same card.
 */
export function useIssueGiftCard(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      body,
      idempotencyKey,
    }: {
      body: PostAdminV1StoresStoreIdGiftCardsBody;
      idempotencyKey: string;
    }) => giftCardsApi.issue(storeId, body, idempotencyKey),
    meta: { successMessage: 'giftCards:toasts.issued', silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: giftCardKeys.lists(storeId) }),
  });
}

export function useUpdateGiftCard(storeId: string, successMessage: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchAdminV1StoresStoreIdGiftCardsIdBody }) =>
      giftCardsApi.update(storeId, id, body),
    meta: { successMessage, silentError: true },
    onSuccess: (card: GiftCard) => {
      queryClient.setQueryData(giftCardKeys.detail(storeId, card.id), card);
      return queryClient.invalidateQueries({ queryKey: giftCardKeys.lists(storeId) });
    },
  });
}

/** The ledger gains an `adjust` row, so it is refetched with the card. */
export function useAdjustGiftCard(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: PostAdminV1StoresStoreIdGiftCardsIdAdjustBody;
    }) => giftCardsApi.adjust(storeId, id, body),
    meta: { successMessage: 'giftCards:toasts.adjusted', silentError: true },
    onSuccess: async (card: GiftCard) => {
      queryClient.setQueryData(giftCardKeys.detail(storeId, card.id), card);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: giftCardKeys.transactions(storeId, card.id) }),
        queryClient.invalidateQueries({ queryKey: giftCardKeys.lists(storeId) }),
      ]);
    },
  });
}

/** Nothing on the card changes; the email is queued on the backend. */
export function useSendGiftCard(storeId: string) {
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PostAdminV1StoresStoreIdGiftCardsIdSendBody }) =>
      giftCardsApi.send(storeId, id, body),
    meta: { silentError: true },
  });
}
